import {
    ApolloError,
    DocumentNode,
    useLazyQuery,
    useMutation,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ExportGeneratedReports } from 'api/graphQL/queries/projectReport.queries'
import { ExportResult } from 'api/graphQL/types/export.types'
import { useMemo } from 'react'
import { formatLocalDateTime } from 'shared/utils/dateUtils'
import XLSX from 'xlsx-js-style'
import { ExportedFileResults } from 'xspectation/model/fileExport'
import {
    downloadFile,
    getDateForFileName,
} from '../../../xspectation/shared/utils/fileExport'

interface useExportProps {
    onError?: (error: ApolloError) => void
    onCompleted?: () => void
    datatype: 'SmartReport'
    projectName?: string
}

interface contextualBehaviorProps {
    query: DocumentNode
    datatype: 'SmartReport'
    shortDesc: string
    fileNamePrefix: string
    getData: (result: any) => ExportedFileResults
}

const exportGeneratedReportsContextualBehavior: contextualBehaviorProps = {
    query: ExportGeneratedReports,
    datatype: 'SmartReport',
    shortDesc: 'exported report rows',
    fileNamePrefix: 'Report',
    getData: (result: any) => ({
        file: result.exportGeneratedReports.file,
        totalCount: result.exportGeneratedReports.totalCount,
    }),
}

// Helper function to update the LastUpdated column
const updateLastUpdatedColumn = (data: any[]) => {
    return data.map((item) => {
        if (
            item.hasOwnProperty('Last Updated') &&
            item['Last Updated'] !== '-'
        ) {
            const dateMatch = item['Last Updated'].match(/(.*?)-\s*(.+)$/)
            if (dateMatch) {
                const textBeforeDate = dateMatch[1].trim()
                const dateString = dateMatch[2].trim()
                const formattedDate = formatLocalDateTime(dateString)
                item['Last Updated'] = `${textBeforeDate} - ${formattedDate}`
            }
        }
        return item
    })
}

const updateDataDescriptionColumn = (data: any[]) => {
    return data.map((item) => {
        if (
            item.hasOwnProperty('Data Description') &&
            typeof item['Data Description'] === 'string'
        ) {
            item['Data Description'] = item['Data Description'].replace(
                /\r\n/g,
                '\n'
            )
        }
        return item
    })
}

// BUG 2280494: Remove TAG column from export data
// Handles both cases: data with Tag property and data without
export const removeTagColumn = (data: any[]) => {
    return data.map(({ Tag, ...rest }) => rest)
}

// Function to process SmartReport data
const processSmartReportData = (base64Data: string) => {
    const binaryString = atob(base64Data)
    const arrayBuffer = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
        arrayBuffer[i] = binaryString.charCodeAt(i)
    }
    const workbook = XLSX.read(arrayBuffer, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    const worksheet = workbook.Sheets[sheetName]
    const jsonData = XLSX.utils.sheet_to_json(worksheet)
    const updatedData = updateLastUpdatedColumn(jsonData)
    const dataWithoutTag = removeTagColumn(updatedData)
    const finalData = updateDataDescriptionColumn(dataWithoutTag)
    const newWorksheet = XLSX.utils.json_to_sheet(finalData)

    const columnWidths = Array(Object.keys(finalData[0]).length).fill({
        wpx: 200,
    })
    newWorksheet['!cols'] = columnWidths

    if (newWorksheet['!ref']) {
        const range = XLSX.utils.decode_range(newWorksheet['!ref'])
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
                if (newWorksheet[cellAddress]) {
                    newWorksheet[cellAddress].s = {
                        alignment: {
                            wrapText: true,
                            vertical: 'center',
                            horizontal: 'left',
                        },
                    }
                    if (R === 0) {
                        newWorksheet[cellAddress].s.font = { bold: true }
                    }
                }
            }
        }
    }

    const newWorkbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, sheetName)
    return XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'binary' })
}

// Hook used to export query results as Excel for SmartReport
const useExportSmartReportAsExcel = ({
    onCompleted,
    onError,
    datatype,
    projectName,
}: useExportProps) => {
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const contextualBehavior: contextualBehaviorProps = useMemo(
        () => exportGeneratedReportsContextualBehavior,
        [datatype]
    )

    const [exportItems, { loading }] = useLazyQuery<ExportResult>(
        contextualBehavior.query,
        {
            fetchPolicy: 'network-only',
            onCompleted: (results) => {
                const data = contextualBehavior.getData(results)
                if (data?.file?.length) {
                    const newWorkbookBinary = processSmartReportData(data.file)
                    const fileData = btoa(newWorkbookBinary)

                    const fileName = `${projectName ? `${projectName} ` : ''}${
                        contextualBehavior.fileNamePrefix
                    }_${getDateForFileName()}.xlsx`
                    downloadFile(
                        fileData,
                        fileName,
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        true
                    )
                    onCompleted?.()
                } else {
                    console.error(
                        `Failed to ${contextualBehavior.shortDesc}: data.file is null`
                    )
                    showGlobalSnackbar({
                        variables: {
                            message: `Failed to ${contextualBehavior.shortDesc}`,
                            variant: 'error',
                            id: `${contextualBehavior.datatype}Export_Error`,
                        },
                    })
                }
            },
            onError: (error) => {
                console.error(
                    `Failed to ${contextualBehavior.shortDesc}: ${error}`
                )
                onError?.(error)
                showGlobalSnackbar({
                    variables: {
                        message: `Failed to ${contextualBehavior.shortDesc}`,
                        variant: 'error',
                        id: `${contextualBehavior.datatype}Export_Error`,
                    },
                })
            },
        }
    )

    return { loading, exportItems }
}

export default useExportSmartReportAsExcel
